import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import FormattedPrice from '@components/FormattedPrice'
import { PriceBreakdown } from '@components/PriceInfo/PriceBreakdown'
import { PriceExplanationMark } from '@components/PriceInfo/PriceExplanationMark'
import { getBasePrice, getPriceOrNull } from '../../helpers'
import { useBookingFormStore } from '../../providers/BookingFormStoreProvider'
import { IPrice } from '../../store/data'
import DiscountedPrice from '../DiscountedPrice'
import Typography from '../Typography'
import styles from './Price.module.scss'

type PriceProps = {
  data: IPrice | null
  // Decimal places for the amounts. Default 0 (whole currency units). The summary step
  // passes 2 so this bar matches the cent-precise breakdown shown above it.
  fractionDigits?: number
  // When true, a discounted price shows the struck-through original price ABOVE the
  // discounted value (read top-to-bottom) instead of inline. Opt-in — used only for the
  // primary sticky price bar (PriceInfo); everywhere else keeps the inline layout.
  stacked?: boolean
  withPriceExplanation?: boolean
}

const Price: React.FunctionComponent<PriceProps> = ({
  data,
  fractionDigits = 0,
  stacked = false,
  withPriceExplanation = false,
}) => {
  const language = useBookingFormStore((state) => state.language)
  const step = useBookingFormStore((state) => state.stepper.step)
  const amount = data ? getPriceOrNull(data, 'amount', language) : null
  // Original = the full base price (before ALL discounts — loyalty + promotions), so
  // the strikethrough matches "Základní cena" and the struck-out saving equals the
  // "Slevy" line. Equals `amount` when there is no discount → no strikethrough.
  const original = amount !== null && data ? getBasePrice(data, language) : null

  const showPriceBreakdown = step === 4
  const hasDiscount = amount !== null && typeof original === 'number' && original > amount
  const showStacked = stacked && hasDiscount && !showPriceBreakdown

  if (showPriceBreakdown) {
    return (
      <div className={styles.root}>
        <PriceBreakdown data={data} />
      </div>
    )
  }

  // The discounted value + "(incl. VAT)" row — identical layout whether stacked or inline.
  // When stacked the value is accented green here (the original moves above), so
  // DiscountedPrice is only used for the inline layout.
  const valueRow = (
    <div className={styles.root}>
      <Typography className={styles.rootPrice} component="span" variant="category">
        {amount !== null ? (
          <>
            {showStacked ? (
              <span className="text-[#28a745]">
                <FormattedPrice fractionDigits={fractionDigits} value={amount} />
              </span>
            ) : (
              <DiscountedPrice
                fractionDigits={fractionDigits}
                original={original}
                value={amount}
              />
            )}
            {withPriceExplanation && <PriceExplanationMark />}
          </>
        ) : (
          '-'
        )}
      </Typography>
      <Typography className={styles.rootDescription} component="span">
        <FormattedMessage defaultMessage="(incl. VAT)" description="Text in Price component" />
      </Typography>
    </div>
  )

  if (!showStacked) {
    return valueRow
  }

  return (
    <div className={styles.rootStacked}>
      <Typography className={styles.rootOriginal} component="span" variant="subcategory">
        <FormattedPrice fractionDigits={fractionDigits} value={original as number} />
      </Typography>
      {valueRow}
    </div>
  )
}

export default Price
